import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Survey from '../components/Survey';
import ConsultationModal from '../components/ConsultationModal';
import Testimonials from '../components/Testimonials';
import img1 from '../img/1.png';
import img12 from '../img/12.png';
import img18 from '../img/18.png';
import heroBlackMan from '../img/hero-black-man.jpg';

function Home() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const galleryImages = [
    { image: img1, description: 'Natural density and a finished everyday look' },
    { image: img12, description: 'Custom color, texture, and blending' },
    { image: img18, description: 'Coverage that moves with you' },
  ];

  return (
    <div className="min-h-screen">
      <section className="relative flex min-h-[calc(100vh-6rem)] items-end overflow-hidden bg-surface-dark text-white md:items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={heroBlackMan}
            alt="Hair replacement transformation — Black male client"
            className="h-full w-full object-cover object-[center_20%] animate-hero-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-surface-dark via-surface-dark/80 to-primary-900/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-surface-dark/95 via-transparent to-surface-dark/25" />
        </div>

        <div className="container relative z-10 mx-auto px-4 pb-16 pt-10 md:py-24">
          <div className="max-w-2xl">
            <p className="animate-fade-up font-display text-3xl font-semibold tracking-wide text-white sm:text-4xl md:text-5xl">
              Sheryl Williams
            </p>
            <p
              className="mt-2 animate-fade-up text-sm font-semibold uppercase tracking-[0.22em] text-primary-200"
              style={{ animationDelay: '120ms' }}
            >
              Hair Replacement
            </p>
            <h1
              className="mt-6 animate-fade-up font-display text-4xl font-semibold leading-tight sm:text-5xl md:text-6xl"
              style={{ animationDelay: '200ms' }}
            >
              Natural-looking hair replacement, privately guided.
            </h1>
            <p
              className="mt-5 max-w-xl animate-fade-up text-lg leading-relaxed text-white/85 md:text-xl"
              style={{ animationDelay: '320ms' }}
            >
              Custom systems, expert fitting, and ongoing care for people ready to feel like themselves again.
            </p>
            <div
              className="mt-8 flex animate-fade-up flex-col gap-3 sm:flex-row sm:items-center"
              style={{ animationDelay: '440ms' }}
            >
              <button
                type="button"
                className="rounded-full bg-white px-8 py-4 font-semibold text-primary-700 shadow-lg transition duration-300 hover:bg-primary-50"
                onClick={() => setIsModalOpen(true)}
              >
                Book Free Consultation
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/services')}
              >
                Explore Services
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-primary-100 bg-white py-16">
        <div className="container mx-auto grid gap-10 px-4 md:grid-cols-3">
          {[
            {
              title: '20+ years of focus',
              copy: 'Hair replacement is the work — not a side service.',
            },
            {
              title: 'Two studio locations',
              copy: 'Pottstown, PA and Teaneck, NJ for private appointments.',
            },
            {
              title: 'Private first visit',
              copy: 'A free consultation to map options before you commit.',
            },
          ].map((item) => (
            <div key={item.title}>
              <h2 className="font-display text-2xl font-semibold text-ink">{item.title}</h2>
              <p className="mt-3 leading-relaxed text-ink-muted">{item.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-surface py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <p className="section-kicker">Real results</p>
            <h2 className="section-heading mb-4">Transformations that look like you</h2>
            <p className="text-lg text-ink-muted">
              Browse recent work from clients who came for answers — and left with a result they can wear every day.
            </p>
          </div>

          <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {galleryImages.map((item, index) => (
              <figure
                key={item.description}
                className="transformation-card group relative overflow-hidden bg-surface-dark"
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <div className="relative pt-[118%]">
                  <img
                    src={item.image}
                    alt={item.description}
                    className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent px-5 pb-5 pt-16">
                  <p className="text-sm leading-snug text-white/95 sm:text-base">{item.description}</p>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-12 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="btn-primary"
            >
              Schedule Your Consultation
            </button>
            <button
              type="button"
              onClick={() => navigate('/about')}
              className="btn-ghost"
            >
              Meet the Team
            </button>
          </div>
        </div>
      </section>

      <Testimonials />

      <section className="bg-white py-24">
        <div className="container mx-auto px-4">
          <div className="mb-12 max-w-3xl">
            <p className="section-kicker">What we offer</p>
            <h2 className="section-heading">Two clear paths forward</h2>
            <p className="text-lg text-ink-muted">
              Whether you are researching options or ready to begin, start with the path that fits you.
            </p>
          </div>

          <div className="grid gap-10 md:grid-cols-2">
            <div className="border-t-2 border-primary-600 pt-8">
              <h3 className="font-display text-3xl font-semibold text-ink">Learn the process</h3>
              <p className="mt-4 leading-relaxed text-ink-muted">
                See how consultation, custom systems, fitting, blending, and maintenance work together for a natural result.
              </p>
              <button
                type="button"
                onClick={() => navigate('/services')}
                className="mt-6 font-semibold text-primary-700 transition-colors hover:text-primary-900"
              >
                View services →
              </button>
            </div>
            <div className="border-t-2 border-primary-600 pt-8">
              <h3 className="font-display text-3xl font-semibold text-ink">Book a consultation</h3>
              <p className="mt-4 leading-relaxed text-ink-muted">
                A private appointment to discuss coverage, lifestyle, and the system that fits — with no pressure to decide on the spot.
              </p>
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="mt-6 font-semibold text-primary-700 transition-colors hover:text-primary-900"
              >
                Book free consultation →
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-primary-700 py-20 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.12),_transparent_45%)]" />
        <div className="container relative mx-auto px-4 text-center">
          <h2 className="font-display text-4xl font-semibold md:text-5xl">Ready when you are</h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-primary-50">
            Schedule your free consultation and take the first step toward a natural-looking hair replacement solution.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              className="rounded-full bg-white px-8 py-3 font-semibold text-primary-700 transition duration-300 hover:bg-primary-50"
              onClick={() => setIsModalOpen(true)}
            >
              Book Consultation
            </button>
            <a
              href="tel:+12017267342"
              className="rounded-full border border-white/70 px-8 py-3 font-semibold text-white transition duration-300 hover:bg-white/10"
            >
              Call (201) 726-7342
            </a>
          </div>
        </div>
      </section>

      <section className="bg-surface-warm py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <p className="section-kicker">Not ready to book?</p>
            <h2 className="section-heading mb-3">Tell us what you are looking for</h2>
            <p className="text-ink-muted">
              Share a few details and we will follow up with guidance tailored to your goals.
            </p>
          </div>
          <Survey />
        </div>
      </section>

      <ConsultationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}

export default Home;
